export interface IndexingPolicy {
  noindexPaths: string[]
  comparisonProducts: string[]
  protectedComparisons: string[]
  redirectNoindex?: Record<string, boolean>
  noindexAll?: boolean
}

export const EMPTY_INDEXING_POLICY: IndexingPolicy = {
  noindexPaths: [],
  comparisonProducts: [],
  protectedComparisons: [],
}

export const COMPARE_PATH = '/hearing-aids/compare/'
// A pair needs at least 20 finalized Google clicks in the rolling 28-day window
// to override catalog exclusions.
export const COMPARISON_MIN_PROTECTED_CLICKS = 20

// These private routes bypass public story/redirect resolution. Standalone
// auth/tools can recognize them without a published indexing snapshot.
const PRIVATE_ROOTS = ['/admin', '/api', '/auth', '/perf', '/draft-stories']

// Match robots.txt, static page metadata and the standalone iframe documents.
// Prefixes match a path segment, never unrelated names such as /authoritative.
const NOINDEX_ROOTS = [
  ...PRIVATE_ROOTS,
  '/proposals',
  '/style-guide',
  '/score-showcase',
  '/clinics',
  '/onboarding',
  '/press-releases',
  '/forum-ui',
  '/forum-ui-redirect',
  '/price-alerts/manage',
  '/price-alerts/unsubscribe',
  '/price-alerts/unsubscribe-all',
  '/research/thank-you',
  '/advertising-specifications',
  '/hearing-guide',
  '/styles',
  '/_next/static',
]

const isStaticNoindexPath = (path: string) => NOINDEX_ROOTS.some((root) => path === root || path.startsWith(`${root}/`))

export const requiresDocumentUrl = (href: string | undefined): boolean => {
  const value = href?.trim()
  return Boolean(value && !/^(?:[a-z][a-z0-9+.-]*:|\/|#)/i.test(value))
}

export const internalPath = (href: string, documentUrl?: string): string | null => {
  if ((!href && !documentUrl) || /^(#|mailto:|tel:|javascript:|data:)/i.test(href.trim())) {
    return null
  }
  // An unknown document URL cannot safely resolve a relative link.
  if (requiresDocumentUrl(href) && !documentUrl) return null
  try {
    const base = new URL(documentUrl ?? '/', 'https://www.hearingtracker.com/')
    const url = new URL(href, base)
    if (
      !['https:', 'http:'].includes(url.protocol) ||
      !['hearingtracker.com', 'www.hearingtracker.com'].includes(url.hostname)
    ) {
      return null
    }
    return (
      decodeURIComponent(url.pathname)
        .replace(/^\/(en|de|zz)(?=\/|$)/, '')
        .replace(/^\/stories(?=\/|$)/, '')
        .replace(/\/+$/, '') || '/'
    )
  } catch {
    return null
  }
}

export const comparisonPair = (slug: string): [string, string] | null => {
  const parts = slug.split('-vs-')
  if (
    parts.length !== 2 ||
    !parts[0] ||
    !parts[1] ||
    parts[0] === parts[1] ||
    parts.some((part) => !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(part))
  ) {
    return null
  }
  return [parts[0], parts[1]].sort() as [string, string]
}

export const isComparisonIndexable = (slug: string, policy: IndexingPolicy): boolean => {
  const pair = comparisonPair(slug)
  if (!pair) {
    return false
  }
  return (
    policy.protectedComparisons.includes(pair.join('-vs-')) ||
    pair.every((product) => policy.comparisonProducts.includes(product))
  )
}

export const isNoindexPath = (path: string, policy: IndexingPolicy): boolean => {
  if (policy.noindexAll) {
    return true
  }
  if (policy.redirectNoindex?.[path] !== undefined) return policy.redirectNoindex[path]
  if (isStaticNoindexPath(path)) return true
  if (path.startsWith(COMPARE_PATH)) {
    return !isComparisonIndexable(path.slice(COMPARE_PATH.length), policy)
  }
  return policy.noindexPaths.includes(path)
}

export const linkRel = (
  href: string | undefined,
  rel: string | undefined,
  policy: IndexingPolicy | null,
  documentUrl?: string
): string | undefined => {
  const path = href !== undefined ? internalPath(href, documentUrl) : null
  const noindex =
    path &&
    (policy ? isNoindexPath(path, policy) : PRIVATE_ROOTS.some((root) => path === root || path.startsWith(`${root}/`)))
  if (!noindex) {
    return rel
  }
  const tokens = (rel ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !['follow', 'dofollow'].includes(token.toLowerCase()))
  if (!tokens.some((token) => token.toLowerCase() === 'nofollow')) {
    tokens.push('nofollow')
  }
  return tokens.join(' ')
}
